/**
 * Pure, framework-agnostic helpers for the DD checklist's SECTION CERTIFICATION
 * feature (migration 0038) — the "Certified" tick (🟢) and the AI "Classified"
 * tick (🔵). A DD "section" is a CATEGORY within a PHASE; both checklist UIs
 * (web/admin DueDiligenceCard and web/submit ChecklistPage) must form the OPAQUE
 * `section_key` the backend stores IDENTICALLY so the seller's attestation and the
 * admin's read line up, so that key-formation logic lives here ONCE.
 *
 * The convention is `${phaseId}:${categoryTagId}` for a category section and
 * `${phaseId}` for a phase-level / ungrouped bucket — ALWAYS the tag-id form,
 * taken from the request's own `categoryTagId` (the API sends it on every DD
 * request). It is never derived from GET /categories (permission-filtered for
 * the seller, and not loaded on first render) and there is no path-based
 * fallback: the old one joined names with a NUL byte, which the API rejects, and
 * made the seller attest under keys the admin never read. A section with no tag
 * id has no key, and the UI offers no certify control for it.
 *
 * ZERO imports (structural param types only) so it type-checks identically under
 * the apps' bundler resolution and any Node resolution.
 */

/** The minimal shape of a category tree node (GET /categories `CategoryNode`):
 * its `id` (the `tags.id`) and child nodes, keyed for lookup by `name`. */
interface CategoryNodeLike {
  id: string;
  name: string;
  children: readonly CategoryNodeLike[];
}

/** The minimal shape a document needs for the 🔵 Classified derivation. */
interface ClassifiableDocLike {
  classificationStatus?: string;
  categoryTagIds?: string[];
  categoryPath?: string[];
}

/** A category path as an in-memory map/compare key — JSON, so it is unambiguous
 * for any segment text and never contains a control character. Never sent to
 * the API. */
function pathKey(categoryPath: readonly string[]): string {
  return JSON.stringify(categoryPath);
}

/**
 * Index a category tree by full name-path (root → leaf) → the node's tag id, so a
 * checklist item's `categoryPath` resolves to its `category_tag_id` in O(1). The
 * tree is small and shared across a phase's items, so it is built once per render.
 */
export function buildCategoryPathIndex(roots: readonly CategoryNodeLike[]): Map<string, string> {
  const index = new Map<string, string>();
  const walk = (nodes: readonly CategoryNodeLike[], prefix: readonly string[]): void => {
    for (const node of nodes) {
      const path = [...prefix, node.name];
      index.set(pathKey(path), node.id);
      if (node.children.length > 0) walk(node.children, path);
    }
  };
  walk(roots, []);
  return index;
}

/** Resolve a checklist item's category PATH (names) to its `category_tag_id` via
 * a {@link buildCategoryPathIndex} index, or undefined when the path is not in the
 * tree. */
export function resolveCategoryTagId(
  pathIndex: Map<string, string>,
  categoryPath: readonly string[],
): string | undefined {
  return pathIndex.get(pathKey(categoryPath));
}

/**
 * The OPAQUE section key for a CATEGORY section — `${phaseId}:${categoryTagId}`
 * from the section's requests' own `categoryTagId`, or null when there is none.
 * Never call this for the ungrouped bucket — use {@link phaseSectionKey} there.
 */
export function sectionKeyForCategory(
  phaseId: string,
  categoryTagId: string | null | undefined,
): string | null {
  return categoryTagId ? `${phaseId}:${categoryTagId}` : null;
}

/** The section key for a phase-level / ungrouped bucket (`${phaseId}`). */
export function phaseSectionKey(phaseId: string): string {
  return phaseId;
}

/** True when a document has been PLACED into the given category — by tag-id
 * membership (preferred; the admin document list serves `categoryTagIds`) or, as
 * a fallback, an exact `categoryPath` match (for listings that omit the ids). */
function docInCategory(
  doc: ClassifiableDocLike,
  categoryTagId: string | undefined,
  categoryPath: readonly string[],
): boolean {
  if (categoryTagId && doc.categoryTagIds?.includes(categoryTagId)) return true;
  if (categoryPath.length > 0 && doc.categoryPath && pathKey(doc.categoryPath) === pathKey(categoryPath))
    return true;
  return false;
}

/**
 * The 🔵 Classified signal for a section: true iff ≥1 of the room's documents has
 * been classified INTO this section's category. A document counts when it is
 * placed in the category (tag-id membership, or exact path match) AND its AI
 * classification has resolved (not still `processing`, not `failed`) — i.e. the
 * AI identified and placed it.
 */
export function sectionIsClassified(
  docs: readonly ClassifiableDocLike[],
  categoryTagId: string | undefined,
  categoryPath: readonly string[],
): boolean {
  return docs.some(
    (doc) =>
      docInCategory(doc, categoryTagId, categoryPath) &&
      doc.classificationStatus !== 'processing' &&
      doc.classificationStatus !== 'failed',
  );
}
